import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  Fade,
  Switch,
  Button,
  Divider,
} from "@material-ui/core";
import ImportExportIcon from "@material-ui/icons/ImportExport";
import SyncIcon from "@material-ui/icons/Sync";
import PeopleIcon from "@material-ui/icons/People";
import GroupAddIcon from "@material-ui/icons/GroupAdd";
import ListIcon from "@material-ui/icons/List";
import SendIcon from "@material-ui/icons/Send";
import EditIcon from "@material-ui/icons/Edit";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import GroupWorkIcon from "@material-ui/icons/GroupWork";
import WifiOffIcon from "@material-ui/icons/WifiOff";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "auto",
  },

  /* ── Header gradient banner ── */
  headerBanner: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    borderRadius: 0,
    padding: theme.spacing(4, 4, 5),
    color: "#fff",
    position: "relative",
    overflow: "hidden",
    "&::before": {
      content: '""',
      position: "absolute",
      top: -60,
      right: -60,
      width: 200,
      height: 200,
      borderRadius: "50%",
      background: "rgba(255,255,255,0.08)",
    },
    "&::after": {
      content: '""',
      position: "absolute",
      bottom: -40,
      left: "30%",
      width: 140,
      height: 140,
      borderRadius: "50%",
      background: "rgba(255,255,255,0.05)",
    },
  },
  headerTitle: {
    fontWeight: 700,
    fontSize: "1.6rem",
    letterSpacing: "-0.02em",
    marginBottom: theme.spacing(0.5),
  },
  headerSubtitle: {
    opacity: 0.85,
    fontSize: "0.9rem",
    fontWeight: 400,
  },
  connectionSelectWrapper: {
    padding: theme.spacing(2, 4, 0),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  connectionSelect: {
    minWidth: 320,
    maxWidth: 500,
    borderRadius: 10,
    backgroundColor: "#fff",
    "& .MuiSelect-select": {
      fontWeight: 500,
    },
  },
  connectionLabel: {},

  /* ── Content area ── */
  content: {
    padding: theme.spacing(3, 4),
    flex: 1,
  },

  /* ── Stats row ── */
  statsRow: {
    marginBottom: theme.spacing(3),
  },
  statCard: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(2, 3),
    borderRadius: 14,
    background: theme.palette.type === "dark"
      ? "linear-gradient(135deg, rgba(102,126,234,0.15) 0%, rgba(118,75,162,0.10) 100%)"
      : "linear-gradient(135deg, rgba(102,126,234,0.08) 0%, rgba(118,75,162,0.05) 100%)",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(102,126,234,0.25)"
        : "rgba(102,126,234,0.15)"
    }`,
  },
  statIconBox: {
    width: 48,
    height: 48,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    marginRight: theme.spacing(2),
    flexShrink: 0,
  },
  statValue: {
    fontWeight: 700,
    fontSize: "1.5rem",
    lineHeight: 1.2,
    color: theme.palette.text.primary,
  },
  statLabel: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },

  /* ── Action cards ── */
  actionCard: {
    borderRadius: 14,
    cursor: "pointer",
    transition: "transform 0.25s cubic-bezier(0.4,0,0.2,1), box-shadow 0.25s cubic-bezier(0.4,0,0.2,1)",
    border: `1px solid ${theme.palette.divider}`,
    overflow: "hidden",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: theme.palette.type === "dark"
        ? "0 12px 28px rgba(0,0,0,0.4)"
        : "0 12px 28px rgba(0,0,0,0.12)",
    },
  },
  actionCardContent: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(2.5, 3),
    "&:last-child": {
      paddingBottom: theme.spacing(2.5),
    },
  },
  actionIconBox: {
    width: 56,
    height: 56,
    borderRadius: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    marginRight: theme.spacing(2),
  },
  actionTextBlock: {
    flex: 1,
    minWidth: 0,
  },
  actionTitle: {
    fontWeight: 600,
    fontSize: "1rem",
    color: theme.palette.text.primary,
    marginBottom: 2,
  },
  actionDesc: {
    fontSize: "0.82rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.45,
  },
  actionArrow: {
    fontSize: 16,
    color: theme.palette.text.disabled,
    marginLeft: theme.spacing(1),
    flexShrink: 0,
    transition: "transform 0.2s, color 0.2s",
    "$actionCard:hover &": {
      transform: "translateX(3px)",
      color: theme.palette.text.secondary,
    },
  },

  /* ── Empty state ── */
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(8, 2),
  },
  emptyIconRing: {
    width: 120,
    height: 120,
    borderRadius: "50%",
    margin: "0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: theme.palette.type === "dark"
      ? "rgba(255,255,255,0.04)"
      : "rgba(0,0,0,0.03)",
    border: `2px dashed ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.12)"
        : "rgba(0,0,0,0.1)"
    }`,
    marginBottom: theme.spacing(3),
  },
  emptyTitle: {
    fontWeight: 600,
    fontSize: "1.15rem",
    marginBottom: theme.spacing(1),
  },
  emptyHint: {
    fontSize: "0.9rem",
    color: theme.palette.text.secondary,
    maxWidth: 360,
    margin: "0 auto",
    lineHeight: 1.6,
  },

  /* ── Loading ── */
  loadingWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(10),
  },

  /* ── Settings section ── */
  settingsSection: {
    marginTop: theme.spacing(4),
  },
  settingsTitle: {
    fontWeight: 600,
    fontSize: "1.1rem",
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  settingCard: {
    borderRadius: 14,
    border: `1px solid ${theme.palette.divider}`,
    overflow: "hidden",
    marginBottom: theme.spacing(1),
  },
  settingRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.5, 2.5),
    "&:not(:last-child)": {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  settingTextBlock: {
    flex: 1,
    minWidth: 0,
    paddingRight: theme.spacing(2),
  },
  settingName: {
    fontWeight: 600,
    fontSize: "0.92rem",
    color: theme.palette.text.primary,
  },
  settingDesc: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
    marginTop: 2,
  },
  importSection: {
    marginTop: theme.spacing(2),
  },
  importCard: {
    borderRadius: 14,
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2, 2.5),
  },
  importButton: {
    marginTop: theme.spacing(1.5),
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 10,
  },
}));

const CARD_GRADIENTS = [
  "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
  "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
  "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
];

const GroupManagement = () => {
  const classes = useStyles();
  const history = useHistory();

  const [connections, setConnections] = useState([]);
  const [selectedConnectionId, setSelectedConnectionId] = useState("");
  const [loading, setLoading] = useState(true);
  const [groupCount, setGroupCount] = useState(null);
  const [countLoading, setCountLoading] = useState(false);

  // Group sync settings
  const [syncSettings, setSyncSettings] = useState({
    allowGroup: false,
    groupSyncParticipants: false,
    groupSyncMetadata: false,
    groupAutoRegister: false,
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);

  /* ── Fetch connections ── */
  useEffect(() => {
    const fetchConnections = async () => {
      try {
        const { data } = await api.get("/group-management/connections");
        setConnections(data);
        if (data.length > 0 && !selectedConnectionId) {
          setSelectedConnectionId(String(data[0].id));
        }
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchConnections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Fetch group count when connection changes ── */
  const fetchGroupCount = useCallback(async (connId) => {
    if (!connId) {
      setGroupCount(null);
      return;
    }
    setCountLoading(true);
    try {
      const { data } = await api.get(
        `/group-management/connections/${connId}/groups`
      );
      setGroupCount(Array.isArray(data) ? data.length : 0);
    } catch (err) {
      setGroupCount(null);
      toastError(err);
    } finally {
      setCountLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedConnectionId) {
      fetchGroupCount(selectedConnectionId);
      fetchSyncSettings(selectedConnectionId);
    } else {
      setGroupCount(null);
    }
  }, [selectedConnectionId, fetchGroupCount]);

  /* ── Fetch sync settings ── */
  const fetchSyncSettings = async (connId) => {
    if (!connId) return;
    try {
      const { data } = await api.get(`/group-management/connections/${connId}/group-sync`);
      setSyncSettings({
        allowGroup: !!data.allowGroup,
        groupSyncParticipants: !!data.groupSyncParticipants,
        groupSyncMetadata: !!data.groupSyncMetadata,
        groupAutoRegister: !!data.groupAutoRegister,
      });
    } catch (err) {
      // Ignorar — pode ser conexão sem suporte
    }
  };

  /* ── Toggle sync setting ── */
  const handleToggleSetting = async (key) => {
    if (!selectedConnectionId) return;
    const newValue = !syncSettings[key];
    setSyncSettings((prev) => ({ ...prev, [key]: newValue }));
    try {
      await api.put(`/group-management/connections/${selectedConnectionId}/group-sync`, {
        [key]: newValue,
      });
    } catch (err) {
      // Reverter em caso de erro
      setSyncSettings((prev) => ({ ...prev, [key]: !newValue }));
      toastError(err);
    }
  };

  /* ── Import all groups ── */
  const handleImportGroups = async () => {
    if (!selectedConnectionId || importLoading) return;
    setImportLoading(true);
    try {
      const { data } = await api.post(
        `/group-management/connections/${selectedConnectionId}/import-groups`
      );
      const { toast } = await import("react-toastify");
      toast.success(data.message || `${data.imported} grupos importados, ${data.updated} atualizados.`);
      // Atualizar contagem
      fetchGroupCount(selectedConnectionId);
    } catch (err) {
      toastError(err);
    } finally {
      setImportLoading(false);
    }
  };

  /* ── Navigation ── */
  const handleNavigate = (path) => {
    if (!selectedConnectionId) return;
    history.push(`${path}?connectionId=${selectedConnectionId}`);
  };

  const handleConnectionChange = (e) => {
    setSelectedConnectionId(e.target.value);
  };

  /* ── Card definitions ── */
  const actionCards = [
    {
      title: i18n.t("groupManagement.listGroups"),
      description: i18n.t("groupManagement.listGroupsDescription"),
      icon: <ListIcon style={{ fontSize: 28, color: "#fff" }} />,
      path: "/group-management/groups",
      gradient: CARD_GRADIENTS[0],
    },
    {
      title: i18n.t("groupManagement.createGroup"),
      description: i18n.t("groupManagement.createGroupDescription"),
      icon: <GroupAddIcon style={{ fontSize: 28, color: "#fff" }} />,
      path: "/group-management/groups/new",
      gradient: CARD_GRADIENTS[1],
    },
    {
      title: i18n.t("groupManagement.broadcast"),
      description: i18n.t("groupManagement.broadcastDescription"),
      icon: <SendIcon style={{ fontSize: 28, color: "#fff" }} />,
      path: "/group-management/broadcast",
      gradient: CARD_GRADIENTS[2],
    },
    {
      title: i18n.t("groupManagement.editGroup"),
      description: i18n.t("groupManagement.editGroupDescription"),
      icon: <EditIcon style={{ fontSize: 28, color: "#fff" }} />,
      path: "/group-management/groups/edit",
      gradient: CARD_GRADIENTS[3],
    },
  ];

  /* ── Render ── */
  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("groupManagement.title")}</Title>
      </MainHeader>

      <Box className={classes.root}>
        {/* ── Gradient header banner ── */}
        <Paper className={classes.headerBanner} elevation={0}>
          <Typography className={classes.headerTitle}>
            {i18n.t("groupManagement.title")}
          </Typography>
          {connections.length > 1 && (
            <Typography className={classes.headerSubtitle}>
              {i18n.t("groupManagement.selectConnection")}
            </Typography>
          )}

        </Paper>

        {/* ── Connection selector (fora do banner para melhor visibilidade) ── */}
        {connections.length > 0 && (
          <Box className={classes.connectionSelectWrapper}>
            <Typography variant="subtitle2" style={{ fontWeight: 600, color: "#555", whiteSpace: "nowrap" }}>
              Conexão:
            </Typography>
            <FormControl variant="outlined" size="small">
              <Select
                className={classes.connectionSelect}
                value={selectedConnectionId}
                onChange={handleConnectionChange}
                displayEmpty
                disabled={loading}
              >
                {connections.map((conn) => (
                  <MenuItem key={conn.id} value={String(conn.id)}>
                    {conn.name} ({conn.channel}) — {conn.number || "-"}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {/* ── Content area ── */}
        <Box className={classes.content}>
          {loading ? (
            <Box className={classes.loadingWrapper}>
              <CircularProgress size={40} />
            </Box>
          ) : connections.length === 0 ? (
            /* ── Empty state ── */
            <Fade in timeout={500}>
              <Box className={classes.emptyState}>
                <Box className={classes.emptyIconRing}>
                  <WifiOffIcon
                    style={{ fontSize: 52, opacity: 0.35 }}
                    color="disabled"
                  />
                </Box>
                <Typography className={classes.emptyTitle}>
                  {i18n.t("groupManagement.noConnectionsTitle")}
                </Typography>
                <Typography className={classes.emptyHint}>
                  {i18n.t("groupManagement.noConnectionsHint")}
                </Typography>
              </Box>
            </Fade>
          ) : (
            <Fade in timeout={400}>
              <div>
                {/* ── Stats row ── */}
                {selectedConnectionId && (
                  <Box className={classes.statsRow}>
                    <Paper className={classes.statCard} elevation={0}>
                      <Box className={classes.statIconBox}>
                        <GroupWorkIcon
                          style={{ fontSize: 24, color: "#fff" }}
                        />
                      </Box>
                      <Box>
                        <Typography className={classes.statValue}>
                          {countLoading ? (
                            <CircularProgress size={20} />
                          ) : groupCount !== null ? (
                            groupCount
                          ) : (
                            "—"
                          )}
                        </Typography>
                        <Typography className={classes.statLabel}>
                          {i18n.t("groupManagement.totalGroups")}
                        </Typography>
                      </Box>
                    </Paper>
                  </Box>
                )}

                {/* ── Action cards grid ── */}
                <Grid container spacing={2}>
                  {actionCards.map((card, idx) => (
                    <Grid item xs={12} sm={6} key={card.path}>
                      <Card
                        className={classes.actionCard}
                        elevation={0}
                        onClick={() => handleNavigate(card.path)}
                      >
                        <CardContent className={classes.actionCardContent}>
                          <Box
                            className={classes.actionIconBox}
                            style={{ background: card.gradient }}
                          >
                            {card.icon}
                          </Box>
                          <Box className={classes.actionTextBlock}>
                            <Typography className={classes.actionTitle}>
                              {card.title}
                            </Typography>
                            <Typography className={classes.actionDesc}>
                              {card.description}
                            </Typography>
                          </Box>
                          <ArrowForwardIosIcon
                            className={classes.actionArrow}
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                {/* ── Group Sync Settings ── */}
                {selectedConnectionId && (
                  <Box className={classes.settingsSection}>
                    <Typography className={classes.settingsTitle}>
                      <SyncIcon style={{ fontSize: 22 }} />
                      Configurações de Sincronização
                    </Typography>

                    <Card className={classes.settingCard} elevation={0}>
                      {/* Monitorar grupos */}
                      <Box className={classes.settingRow}>
                        <Box className={classes.settingTextBlock}>
                          <Typography className={classes.settingName}>
                            Monitorar grupos
                          </Typography>
                          <Typography className={classes.settingDesc}>
                            Quando ativo, mensagens de grupos serão recebidas e novos grupos serão registrados automaticamente.
                          </Typography>
                        </Box>
                        <Switch
                          checked={syncSettings.allowGroup}
                          onChange={() => handleToggleSetting("allowGroup")}
                          color="primary"
                        />
                      </Box>

                      <Divider />

                      {/* Sync participantes em tempo real */}
                      <Box className={classes.settingRow}>
                        <Box className={classes.settingTextBlock}>
                          <Typography className={classes.settingName}>
                            Sync participantes em tempo real
                          </Typography>
                          <Typography className={classes.settingDesc}>
                            Atualizar membros automaticamente quando alguém entra, sai ou muda de cargo no grupo.
                          </Typography>
                        </Box>
                        <Switch
                          checked={syncSettings.groupSyncParticipants}
                          onChange={() => handleToggleSetting("groupSyncParticipants")}
                          color="primary"
                        />
                      </Box>

                      {/* Sync metadados automaticamente */}
                      <Box className={classes.settingRow}>
                        <Box className={classes.settingTextBlock}>
                          <Typography className={classes.settingName}>
                            Sync metadados automaticamente
                          </Typography>
                          <Typography className={classes.settingDesc}>
                            Atualizar nome, foto e descrição do grupo quando alterados no WhatsApp.
                          </Typography>
                        </Box>
                        <Switch
                          checked={syncSettings.groupSyncMetadata}
                          onChange={() => handleToggleSetting("groupSyncMetadata")}
                          color="primary"
                        />
                      </Box>

                      {/* Auto-registrar novos grupos */}
                      <Box className={classes.settingRow}>
                        <Box className={classes.settingTextBlock}>
                          <Typography className={classes.settingName}>
                            Auto-registrar novos grupos
                          </Typography>
                          <Typography className={classes.settingDesc}>
                            Registrar grupo imediatamente quando este número for adicionado, antes da primeira mensagem.
                          </Typography>
                        </Box>
                        <Switch
                          checked={syncSettings.groupAutoRegister}
                          onChange={() => handleToggleSetting("groupAutoRegister")}
                          color="primary"
                        />
                      </Box>
                    </Card>

                    {/* Importar grupos */}
                    <Box className={classes.importSection}>
                      <Card className={classes.importCard} elevation={0}>
                        <Typography className={classes.settingName}>
                          Importar grupos do WhatsApp
                        </Typography>
                        <Typography className={classes.settingDesc}>
                          Busca todos os grupos que este número participa no WhatsApp. Grupos novos serão criados e existentes terão os dados atualizados.
                        </Typography>
                        <Button
                          variant="contained"
                          color="primary"
                          className={classes.importButton}
                          onClick={handleImportGroups}
                          disabled={importLoading}
                          startIcon={importLoading ? <CircularProgress size={18} /> : <ImportExportIcon />}
                        >
                          {importLoading ? "Importando..." : "Importar Grupos"}
                        </Button>
                      </Card>
                    </Box>
                  </Box>
                )}
              </div>
            </Fade>
          )}
        </Box>
      </Box>
    </MainContainer>
  );
};

export default GroupManagement;
